import { countRemaining } from "@cloudnest/stone-flicking-core-engine/utils";
import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { GameCanvas } from "./GameCanvas";
import { AUGMENTED_STONE_FLICKING_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  type AsfReplaySnapshot,
  buildAugmentedStoneFlickingReplaySnapshots,
} from "./replay-runner";
import "./styles/asf-tokens.css";
import "./asf-animations.css";

interface ReplayScreenProps {
  readonly scenario: string;
}

const PHASE_LABELS: Readonly<Record<string, string>> = {
  PLAYING: "Playing",
  RESOLVING: "Resolving",
  GAME_END: "Game over",
};

function actorName(
  snapshot: AsfReplaySnapshot,
  namesByPlayerId: Readonly<Record<string, string>>,
): string {
  return namesByPlayerId[snapshot.event.userId] ?? snapshot.event.userId;
}

function eventSummary(snapshot: AsfReplaySnapshot): string {
  const suffix =
    snapshot.replayEvent.eventType === "action"
      ? ` ${snapshot.replayEvent.payload.type}`
      : "";
  return `#${snapshot.event.sequenceNumber} ${snapshot.event.eventType}${suffix}`;
}

function clampStepIndex(
  index: number,
  snapshots: readonly AsfReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly AsfReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;

  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }

  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);

  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly AsfReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;

  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = AUGMENTED_STONE_FLICKING_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () =>
      fixture ? buildAugmentedStoneFlickingReplaySnapshots(fixture.events) : [],
    [fixture],
  );
  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(
            fixture.players.map((player) => [player.id, player.name]),
          )
        : {},
    [fixture],
  );
  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            Replay could not be found
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            Close
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const maxIndex = snapshots.length - 1;
  const phaseLabel = PHASE_LABELS[state.phase] ?? state.phase;
  const isGameOver = state.phase === "GAME_END";
  const remainingByPlayer = state.coreState.participants.map((participant) => ({
    id: participant.id,
    name: namesByPlayerId[participant.id] ?? participant.name,
    count: countRemaining(state.coreState.entities, participant.id),
  }));

  return (
    <div
      className="min-h-screen bg-surface-primary"
      data-testid="augmented-stone-flicking-replay-screen"
    >
      <div
        className="sticky top-0 z-20 border-border-primary border-b bg-surface-primary/95 px-4 py-3 backdrop-blur"
        data-testid="augmented-stone-flicking-replay-toolbar"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-semibold text-sm text-text-primary"
              data-testid="augmented-stone-flicking-replay-event-summary"
            >
              {eventSummary(current)} · {actorName(current, namesByPlayerId)}
            </p>
            <p
              className="text-text-secondary text-xs"
              data-testid="augmented-stone-flicking-replay-phase"
            >
              {phaseLabel} · Round {state.round}
              {isGameOver && state.winner ? ` · Winner ${state.winner}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="augmented-stone-flicking-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => setReplayStep(safeStepIndex - 1)}
            >
              Prev
            </button>
            <input
              type="range"
              min={0}
              max={maxIndex}
              value={safeStepIndex}
              className="w-36 md:w-56"
              data-testid="augmented-stone-flicking-replay-step-slider"
              onChange={(event) => setReplayStep(Number(event.target.value))}
            />
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="augmented-stone-flicking-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => setReplayStep(safeStepIndex + 1)}
            >
              Next
            </button>
            <span
              className="w-16 text-right font-semibold text-sm text-text-secondary"
              data-testid="augmented-stone-flicking-replay-step-count"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-5xl gap-4 p-4 md:grid-cols-[220px_1fr]">
        <aside className="space-y-3">
          <div className="rounded-md border border-border-primary bg-surface-secondary p-3">
            <p className="mb-2 font-semibold text-sm text-text-primary">
              Stones
            </p>
            <div className="space-y-2">
              {remainingByPlayer.map((player) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="font-medium text-text-primary">
                    {player.name}
                  </span>
                  <span className="text-text-secondary">{player.count}</span>
                </div>
              ))}
            </div>
          </div>
          {isGameOver && (
            <div
              className="rounded-md border border-border-primary bg-surface-secondary p-3 text-center font-semibold text-sm text-text-primary"
              data-testid="augmented-stone-flicking-replay-game-over"
            >
              Game over{state.winner ? ` · Winner ${state.winner}` : ""}
            </div>
          )}
        </aside>
        <div className="flex justify-center rounded-md border border-border-primary bg-surface-secondary p-4">
          <GameCanvas
            entities={state.coreState.entities}
            selectedEntityId={null}
            isAnimating={false}
            animationRecording={null}
            myPlayerId="P1"
            playerCount={state.playerCount}
            readyPlayers={state.submittedPlayers}
            spaceConfig={current.config.board}
            canInteract={false}
            canvasWidth={520}
            canvasHeight={520}
            canvasPadding={22}
            externalFlickPreview={null}
            onSelectEntity={() => undefined}
            onFlick={() => undefined}
            onAnimationComplete={() => undefined}
          />
        </div>
      </div>
    </div>
  );
}
